import { z } from 'zod';

/**
 * Motifs d'écart proposés à la case à cocher (demande du 2026-09-19 : « en cas
 * de bouton Écarter, demander motif »). La liste est volontairement courte et
 * fermée ; tout le reste se dit dans « Autre ».
 *
 * L'ÂGE N'Y FIGURE PAS, ni le sexe, la situation familiale, la santé, la
 * nationalité ou la religion (CLAUDE.md § Interdits, docs/SECURITE_RGPD.md) :
 * l'application ne les stocke pas et ne s'en sert jamais comme critère. Un
 * écart motivé par l'un d'eux serait une discrimination écrite noir sur blanc
 * dans le dossier. Ce qui se dit de la disponibilité ou de l'expérience se
 * coche ci-dessous ; le reste se rédige à la main, sous la responsabilité de
 * son auteur.
 */
export const MOTIFS_ECART = [
  'residence_eloignee',
  'specialite_hors_sujet',
  'profil_etudiant',
  'experience_insuffisante',
  'disponibilite',
  'pretentions_salariales',
  'injoignable',
  'poste_pourvu',
  'candidature_double',
] as const;

export const motifEcartSchema = z.enum(MOTIFS_ECART);
export type MotifEcart = z.infer<typeof motifEcartSchema>;

export const LIBELLES_MOTIF_ECART: Record<MotifEcart, string> = {
  residence_eloignee: 'Lieu de résidence trop éloigné',
  specialite_hors_sujet: 'Spécialité hors sujet',
  profil_etudiant: 'Profil étudiant',
  experience_insuffisante: 'Expérience insuffisante pour le poste',
  disponibilite: 'Disponibilité incompatible (horaires, date d’entrée)',
  pretentions_salariales: 'Prétentions salariales hors budget',
  injoignable: 'Injoignable après plusieurs tentatives',
  poste_pourvu: 'Poste déjà pourvu',
  candidature_double: 'Candidature en double',
};

/** Longueur du texte libre « Autre », bornée par la colonne `motif_sortie`. */
export const MOTIF_LIBRE_MAX = 300;

export function estMotifEcart(valeur: unknown): valeur is MotifEcart {
  return typeof valeur === 'string' && (MOTIFS_ECART as readonly string[]).includes(valeur);
}

/**
 * Texte enregistré dans `motif_sortie` : les cases cochées dans l'ordre de la
 * liste, puis le texte libre. C'est lui que montrent la fiche, le comparateur
 * et les exports ; les codes cochés restent à part dans `motifs_sortie`.
 */
export function composerMotifEcart(
  motifs: readonly MotifEcart[],
  autre?: string | null,
): string {
  const coches = MOTIFS_ECART.filter((m) => motifs.includes(m)).map((m) => LIBELLES_MOTIF_ECART[m]);
  const libre = (autre ?? '').trim();
  return [...coches, ...(libre ? [libre] : [])].join(' · ');
}

/** Motifs cochés, dédoublonnés et remis dans l'ordre de la liste. */
export function normaliserMotifs(motifs: readonly string[]): MotifEcart[] {
  return MOTIFS_ECART.filter((m) => motifs.includes(m));
}
